import React, { Component } from "react";
import Row from "reactstrap/lib/Row";
import Col from "reactstrap/lib/Col";
import { Button as RSButton, Collapse as RSCollapse, Card, CardBody } from 'reactstrap';
import { Table, TableHead, TableBody, TableRow, TableCell, Paper, IconButton, Chip } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import InputFeild from "../../global/form/input"
import SelectList from "../../global/form/select"
import GoogleAddress from "../../global/google-address";
import ConfirmAction from "../../global/confirm-action";
import ApiEndpoints from "../../../constants/api_endpoints";
import store from "../../../store";
import { setError, setSuccess, setLoader } from "../../../actions/action-creators";
import { Circle, Map } from "google-maps-react";

// location_type (CITY/PINCODE/AREA) is a display/organizational label.
// lat/long/range are the actual geofence data (center + radius in km) —
// same GoogleAddress + Circle pattern job_location already uses in
// manage_location.js, reused here since lead-mapping needs real
// coordinates, not just text.
//
// Code identifiers (class/methods/state/API namespace) stay "JobFieldLocation"
// to match the backend's job_field_location naming — only the strings a
// human actually sees say "Project", matching how the rest of the webapp UI
// already calls this entity "Project" (Manage Project, Project Info, etc.)
// even though the underlying prop/data is still named `job`.
const LOCATION_TYPES = [
    { value: "CITY", label: "City" },
    { value: "PINCODE", label: "Pincode" },
    { value: "AREA", label: "Area" },
];

const formatLocation = (location) => {
    if (location.location_type === "CITY") return location.city;
    if (location.location_type === "PINCODE") return `Pincode ${location.zip}, ${location.city}`;
    if (location.location_type === "AREA") return `${location.area_name}, Pincode ${location.zip}, ${location.city}`;
    return "-";
}

const typeLabel = (location_type) => (LOCATION_TYPES.find(t => t.value === location_type) || {}).label || location_type;

class ManageJobFieldLocations extends Component {
    constructor(props) {
        super(props);
        this.state = {
            jobFieldLocations: [],
            isOpenAdd: false,
            newLocation: {},
            editLocationId: null,
            editLocation: {},
            isOpenConfirm: false,
            isShowMap: false,
        }
    }

    componentDidMount() {
        this.fetchJobFieldLocations();
    }

    fetchJobFieldLocations = async () => {
        store.dispatch(setLoader(true));
        try {
            let resp = await ApiEndpoints.JobFieldLocation.fetch({ job_id: this.props.job.job_id });
            this.setState({ jobFieldLocations: resp.data.data });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    addJobFieldLocation = async (e) => {
        e.preventDefault();
        let { newLocation } = this.state;
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.add({ ...newLocation, job_id: this.props.job.job_id });
            await this.fetchJobFieldLocations();
            this.setState({ isOpenAdd: false, newLocation: {} });
            store.dispatch(setSuccess({ show: true, message: "Project Field Location added!" }))
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    startEdit = (location) => {
        this.setState({ editLocationId: location.job_field_location_id, editLocation: { ...location } });
    }

    saveEdit = async (e) => {
        e.preventDefault();
        let { editLocationId, editLocation } = this.state;
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.edit({ ...editLocation, job_field_location_id: editLocationId });
            await this.fetchJobFieldLocations();
            this.setState({ editLocationId: null, editLocation: {} });
            store.dispatch(setSuccess({ show: true, message: "Project Field Location updated!" }))
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    deleteJobFieldLocation = async (job_field_location_id) => {
        store.dispatch(setLoader(true));
        try {
            await ApiEndpoints.JobFieldLocation.inactive({ job_field_location_id });
            await this.fetchJobFieldLocations();
            this.setState({ isOpenConfirm: false });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
        store.dispatch(setLoader(false))
    }

    // Spread across 3 rows so nothing feels cramped:
    // Row 1: Location Title + Type
    // Row 2: Address (wide, geofence center) + Range
    // Row 3: City + type-specific fields (Pincode for PINCODE/AREA, Area Name for AREA)
    renderLocationFields = (location, onChange) => (
        <>
            <Row className="align-items-center mb-3">
                <Col xs={7}>
                    <InputFeild required placeholder="Location Title" value={location.location_title || ""} onChange={(e) => onChange({ location_title: e.target.value })} />
                </Col>
                <Col xs={4}>
                    <SelectList placeholder="Location Type" value={location.location_type || ""} onChange={(e) => onChange({ location_type: e.target.value })}>
                        {LOCATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </SelectList>
                </Col>
            </Row>
            <Row className="align-items-center mb-3">
                <Col xs={9}>
                    <GoogleAddress
                        defaultValue={location.address}
                        placeholder="Search address to set geofence center"
                        onPlaceChange={(place) => onChange({ address: place.address, city: place.city, zip: place.zip, lat: place.lat, long: place.long })}
                    />
                </Col>
                <Col xs={2}>
                    <InputFeild required type="number" placeholder="Range (Km)" value={location.range || ""} onChange={(e) => onChange({ range: e.target.value })} />
                </Col>
            </Row>
            <Row className="align-items-center">
                <Col xs={3}>
                    <InputFeild required placeholder="City" value={location.city || ""} onChange={(e) => onChange({ city: e.target.value })} />
                </Col>
                {(location.location_type === "PINCODE" || location.location_type === "AREA") &&
                    <Col xs={3}>
                        <InputFeild required placeholder="Pincode" value={location.zip || ""} onChange={(e) => onChange({ zip: e.target.value })} />
                    </Col>
                }
                {location.location_type === "AREA" &&
                    <Col xs={3}>
                        <InputFeild required placeholder="Area / Market Name" value={location.area_name || ""} onChange={(e) => onChange({ area_name: e.target.value })} />
                    </Col>
                }
            </Row>
        </>
    )

    render() {
        let { jobFieldLocations, isOpenAdd, newLocation, editLocationId, editLocation, isShowMap } = this.state;
        const mapCenter = { lat: 19.0760, lng: 72.8777 };
        return (
            <div>
                <Row className="align-items-center mb-3">
                    <Col xs="auto">
                        <RSButton className="buttonred" style={{ height: "30px", paddingTop: "4px" }} onClick={() => this.setState({ isOpenAdd: true })}>
                            <Icon className="align-middle mr-1" style={{ fontSize: 16 }}>add_location</Icon> Add Project Field Location
                        </RSButton>
                    </Col>
                    <Col xs="auto" className="ml-auto">
                        <Icon style={{ cursor: "pointer" }} onClick={() => this.setState({ isShowMap: !isShowMap })}>map</Icon>
                    </Col>
                </Row>
                {isShowMap &&
                    <div style={{ height: 320 }} className="text-center mb-3">
                        <Map initialCenter={mapCenter} google={window.google} style={{ width: "60%", height: 300, left: "20%" }} zoom={11}>
                            {jobFieldLocations.map(loc =>
                                <Circle
                                    key={loc.job_field_location_id}
                                    radius={loc.range * 1000}
                                    center={{ lat: parseFloat(loc.lat), lng: parseFloat(loc.long) }}
                                    strokeColor='transparent'
                                    strokeOpacity={0}
                                    fillColor={'#f1061c'}
                                    fillOpacity={0.35}
                                />
                            )}
                        </Map>
                    </div>
                }
                <RSCollapse isOpen={isOpenAdd}>
                    <Card className="mb-3 shadow-sm">
                        <CardBody>
                            <form onSubmit={this.addJobFieldLocation}>
                                {this.renderLocationFields(newLocation, (patch) => this.setState({ newLocation: { ...newLocation, ...patch } }))}
                                <Row className="justify-content-end mt-3">
                                    <Col xs="auto">
                                        <RSButton className="buttonred mr-2" style={{ height: "30px", paddingTop: "4px" }}>Save</RSButton>
                                        <RSButton onClick={() => this.setState({ isOpenAdd: false, newLocation: {} })} type="button" color="secondary" style={{ height: "30px", paddingTop: "4px" }}>Cancel</RSButton>
                                    </Col>
                                </Row>
                            </form>
                        </CardBody>
                    </Card>
                </RSCollapse>

                <Paper elevation={1}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ background: "#fafafa" }}>
                                <TableCell style={{ fontWeight: 600 }}>Title</TableCell>
                                <TableCell style={{ fontWeight: 600, width: 100 }}>Type</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Coverage</TableCell>
                                <TableCell style={{ fontWeight: 600 }}>Geofence</TableCell>
                                <TableCell style={{ fontWeight: 600 }} align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!jobFieldLocations.length ?
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <em style={{ color: "#999" }}>No Project Field Locations added yet</em>
                                    </TableCell>
                                </TableRow>
                                :
                                jobFieldLocations.map(location =>
                                    editLocationId === location.job_field_location_id ?
                                        <TableRow key={location.job_field_location_id}>
                                            <TableCell colSpan={5}>
                                                <form onSubmit={this.saveEdit}>
                                                    {this.renderLocationFields(editLocation, (patch) => this.setState({ editLocation: { ...editLocation, ...patch } }))}
                                                    <Row className="justify-content-end mt-3">
                                                        <Col xs="auto">
                                                            <IconButton size="small" type="submit"><Icon style={{ fontSize: 18 }}>done</Icon></IconButton>
                                                            <IconButton size="small" type="button" onClick={() => this.setState({ editLocationId: null, editLocation: {} })}><Icon style={{ fontSize: 18 }}>close</Icon></IconButton>
                                                        </Col>
                                                    </Row>
                                                </form>
                                            </TableCell>
                                        </TableRow>
                                        :
                                        <TableRow key={location.job_field_location_id} hover>
                                            <TableCell>{location.location_title}</TableCell>
                                            <TableCell><Chip size="small" label={typeLabel(location.location_type)} /></TableCell>
                                            <TableCell>{formatLocation(location)}</TableCell>
                                            <TableCell>
                                                <span className="d-block" style={{ fontSize: 11 }}>{parseFloat(location.lat).toFixed(4)}, {parseFloat(location.long).toFixed(4)}</span>
                                                <span className="d-block text-muted" style={{ fontSize: 11 }}>{location.range} km radius</span>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => this.startEdit(location)}><Icon style={{ fontSize: 18 }}>edit</Icon></IconButton>
                                                <IconButton size="small" onClick={() => this.setState({ isOpenConfirm: () => this.deleteJobFieldLocation(location.job_field_location_id) })}><Icon style={{ fontSize: 18 }}>delete</Icon></IconButton>
                                            </TableCell>
                                        </TableRow>
                                )
                            }
                        </TableBody>
                    </Table>
                </Paper>
                <ConfirmAction isOpen={!!this.state.isOpenConfirm} callback={this.state.isOpenConfirm} onClose={() => this.setState({ isOpenConfirm: false })} />
            </div>
        );
    }
}
export default ManageJobFieldLocations;
